import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, BookOpen, Video, GraduationCap, Tv, Handshake,
  MessageCircle, Compass, Lightbulb, Users, Rocket,
  HandHeart, Package, Droplets, Building2,
  Instagram, Youtube, CalendarDays, Mic, BookMarked, MonitorPlay, ExternalLink,
  School, Brain, BookOpenCheck, Award, Target, Medal, Network,
  Palette, Puzzle, Tent, Camera,
  Link2, Megaphone, Briefcase, BarChart3,
  Clapperboard, PenLine, Headphones, PenTool,
  Globe
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/* ─── Shared Donation View Component ─── */
const DonationView = ({ onBack, backLabel }) => (
  <>
    <DialogHeader>
      <DialogTitle className="text-2xl font-serif font-bold text-gold mb-4 text-center">Salurkan Donasi Terbaik Anda</DialogTitle>
    </DialogHeader>
    <div className="p-4 text-center space-y-6">
      <div className="rounded-xl overflow-hidden border border-gold/20 max-w-[180px] mx-auto">
        <img
          src="https://customer-assets.emergentagent.com/job_clean-menu-design/artifacts/wik7abko_image.png"
          alt="QRIS Donasi"
          className="w-full h-auto"
        />
      </div>
      <div className="bg-white/5 p-4 rounded-xl border border-white/10">
        <p className="text-sm text-muted-foreground mb-1">Transfer Manual</p>
        <p className="text-sm text-gold font-medium mb-1">Bank Syariah Indonesia (BSI)</p>
        <div className="flex items-center justify-center gap-2 mb-1">
          <p className="text-2xl font-mono font-bold text-white tracking-wider select-all">7741114474</p>
        </div>
        <p className="text-xs text-muted-foreground">an. Yayasan Thuba Minal Jannah</p>
      </div>
      <a
        href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20saya%20sudah%20transfer%20donasi%20untuk%20Thuba%20Project"
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Button className="bg-primary text-black hover:bg-[#F3E5AB] font-bold w-full rounded-full">
          Konfirmasi via WhatsApp
        </Button>
      </a>
      <button
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-white transition-colors"
      >
        ← Kembali ke info {backLabel}
      </button>
    </div>
  </>
);

/* ─── Shared CTA (Donasi + Relawan) ─── */
const SharedCTA = ({ onDonate }) => (
  <div className="space-y-3 pt-4 border-t border-white/10">
    <div className="flex flex-col sm:flex-row gap-3">
      <Button
        onClick={onDonate}
        className="flex-1 bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20"
      >
        <Heart className="w-4 h-4 mr-2" /> Donasi
      </Button>
      <a
        href="https://wa.me/6282310001447?text=Assalamualaikum%2C%20Insyallah%20saya%20bersedia%20menjadi%20Relawan%20melalui%20sebab%20Thuba%20Project"
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1"
      >
        <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-5 rounded-xl shadow-lg shadow-green-900/20">
          <MessageCircle className="w-4 h-4 mr-2" /> Gabung Relawan
        </Button>
      </a>
    </div>
  </div>
);

/* ─── Section Header ─── */
const SectionHeader = ({ icon: Icon, children }) => (
  <h3 className="text-lg font-serif font-semibold text-white flex items-center gap-3">
    <Icon className="w-5 h-5 text-gold" /> {children}
  </h3>
);

/* ─── Program List ─── */
const ProgramList = ({ items }) => (
  <div className="space-y-2 bg-white/5 p-4 rounded-xl border border-white/10">
    {items.map((item, i) => (
      <div key={i} className="flex items-center gap-3 text-muted-foreground text-sm">
        <item.icon className="w-4 h-4 text-gold/70 flex-shrink-0" />
        <span>{item.text}</span>
      </div>
    ))}
  </div>
);

/* ─── Tag Grid ─── */
const TagGrid = ({ items, cols = 2 }) => (
  <div className={`grid grid-cols-${cols} gap-2`}>
    {items.map((item, i) => (
      <div key={i} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-center text-muted-foreground text-sm">
        {item}
      </div>
    ))}
  </div>
);

/* ─── Main Component ─── */
const Ecosystem = () => {
  const [activeDialog, setActiveDialog] = useState(null); // 'sosial' | 'dawah' | 'pendidikan' | 'kidz' | 'kemitraan' | 'media'
  const [donationView, setDonationView] = useState(false);

  React.useEffect(() => {
    const onPopState = (e) => {
      if (e.state?.dialog) {
        setActiveDialog(e.state.dialog);
        setDonationView(false);
      } else {
        setActiveDialog(null);
        setDonationView(false);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openDialog = (id) => {
    setActiveDialog(id);
    setDonationView(false);
    window.history.pushState({ dialog: id }, '');
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setDonationView(false);
    if (window.history.state?.dialog) {
      window.history.back();
    }
  };

  const ecosystems = [
    {
      title: "Thuba Sosial",
      icon: Heart,
      description: "Kepedulian melalui aksi kemanusiaan dan bantuan sosial yang amanah dan berkelanjutan",
      dialogId: "sosial"
    },
    {
      title: "Thuba Da'wah",
      icon: BookOpen,
      description: "Dakwah yang berakar pada ilmu dan sesuai dengan Al-Qur'an dan As Sunnah",
      dialogId: "dawah"
    },
    {
      title: "Thuba Media",
      icon: Video,
      description: "Menyebarkan nilai kebaikan melalui konten media yang kreatif, edukatif, dan mencerahkan",
      dialogId: "media"
    },
    {
      title: "Thuba Pendidikan",
      icon: GraduationCap,
      description: "Menguatkan umat melalui pendidikan, pelatihan, adab dan pembentukan karakter",
      dialogId: "pendidikan"
    },
    {
      title: "Thuba Kidz",
      icon: Tv,
      description: "Menumbuhkan iman dan akhlak anak sejak dini melalui tayangan yang menarik dan bermanfaat",
      dialogId: "kidz"
    },
    {
      title: "Thuba Kemitraan",
      icon: Handshake,
      description: "Ruang kolaborasi dan komersial yang dirancang untuk membangun kerja sama strategis secara profesional, berlandaskan nilai amanah, transparansi, dan kebermanfaatan.",
      dialogId: "kemitraan"
    },
  ];

  return (
    <div className="pt-24 min-h-screen bg-[#081c1a] animate-fade-in-up">
      <div className="container mx-auto px-6 py-12">
        <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 text-center">Ekosistem <span className="text-gold">Thuba</span></h1>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-20">
          Sinergi kebaikan yang bergerak di berbagai sektor untuk membangun kemandirian dan kemajuan umat.
        </p>

        <div className="grid gap-8">
          {ecosystems.map((eco, idx) => (
            <div key={idx} className={`flex flex-col md:flex-row items-center gap-8 glass-card p-8 rounded-2xl ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className="flex-shrink-0 w-32 h-32 bg-gold/10 rounded-full flex items-center justify-center border border-gold/20">
                <eco.icon className="w-16 h-16 text-gold" />
              </div>
              <div className={`flex-1 text-center ${idx % 2 === 1 ? 'md:text-right' : 'md:text-left'}`}>
                <h2 className="text-3xl font-serif font-bold text-white mb-4">{eco.title}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">{eco.description}</p>
                <button
                  className="text-gold font-semibold hover:underline"
                  onClick={() => openDialog(eco.dialogId)}
                >
                  Pelajari Selengkapnya →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          THUBA SOSIAL
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'sosial'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Sosial</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Berbagi kebaikan, menguatkan kepedulian.</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Thuba Sosial adalah ekosistem yang bergerak dalam aksi kemanusiaan, bantuan sosial, dan program pemberdayaan masyarakat. Fokusnya bukan hanya memberi, tetapi membangun keberlanjutan.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Lightbulb}>Perencanaan Program</SectionHeader>
                  <ProgramList items={[
                    { icon: HandHeart, text: "Santunan yatim & dhuafa" },
                    { icon: Package, text: "Program berbagi pangan & paket sembako" },
                    { icon: Droplets, text: "Bantuan bencana & tanggap darurat" },
                    { icon: Building2, text: "Program pemberdayaan ekonomi kecil" },
                  ]} />
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Users}>Siapa yang Bisa Terlibat</SectionHeader>
                  <TagGrid items={["Donatur individu", "Relawan", "Perusahaan mitra CSR", "Komunitas sosial"]} />
                </div>

                <SharedCTA onDonate={() => setDonationView(true)} />
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Sosial" />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          THUBA DA'WAH
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'dawah'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Da'wah</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Menyebarkan ilmu, menumbuhkan iman.</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Thuba Dakwah berfokus pada penyebaran ilmu berdasarkan Al-Qur'an dan Sunnah melalui kajian rutin, tabligh akbar, dan pembinaan keislaman.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={BookOpen}>Program</SectionHeader>
                  <ProgramList items={[
                    { icon: CalendarDays, text: "Kajian rutin pekanan" },
                    { icon: Mic, text: "Tabligh akbar & seminar" },
                    { icon: BookMarked, text: "Kajian kitab" },
                    { icon: MonitorPlay, text: "Dakwah via media digital" },
                  ]} />
                </div>

                {/* Ikuti Dakwah CTA */}
                <div className="space-y-3">
                  <SectionHeader icon={Rocket}>Ikuti Dakwah Kami</SectionHeader>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/media" className="flex-1">
                      <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20">
                        <Instagram className="w-5 h-5 mr-2" />
                        Media Thuba
                      </Button>
                    </Link>
                  </div>
                </div>

                <SharedCTA onDonate={() => setDonationView(true)} />
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Da'wah" />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          THUBA MEDIA
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'media'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Media</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Faith. Focus. Create.</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Ekosistem kreatif yang memproduksi konten edukatif, inspiratif, dan dakwah melalui media digital.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Clapperboard}>Konten</SectionHeader>
                  <ProgramList items={[
                    { icon: MonitorPlay, text: "Video kajian" },
                    { icon: PenLine, text: "Artikel & blog" },
                    { icon: Headphones, text: "Podcast" },
                    { icon: PenTool, text: "Desain visual dakwah" },
                  ]} />
                </div>

                {/* Media Sosial CTA */}
                <div className="space-y-3">
                  <SectionHeader icon={Rocket}>Media Sosial Kami</SectionHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <a href="https://instagram.com/thubaproject/" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                        <Instagram className="w-4 h-4 mr-1" /> Instagram
                      </Button>
                    </a>
                    <a href="https://www.youtube.com/@ThubaProject/streams" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                        <Youtube className="w-4 h-4 mr-1" /> YouTube
                      </Button>
                    </a>
                    <a href="https://tiktok.com/@thuba.project" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" /></svg> TikTok
                      </Button>
                    </a>
                    <a href="https://facebook.com/thubaproject" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-4 rounded-xl shadow-lg shadow-gold/20 text-sm">
                        <Globe className="w-4 h-4 mr-1" /> Facebook
                      </Button>
                    </a>
                  </div>
                </div>

                <SharedCTA onDonate={() => setDonationView(true)} />
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Media" />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          THUBA PENDIDIKAN
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'pendidikan'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Pendidikan</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Membangun generasi berilmu dan berakhlak.</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Ekosistem pendidikan formal dan nonformal untuk membangun pemahaman agama yang benar, serta penguatan karakter dan intelektual.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={School}>Program</SectionHeader>
                  <ProgramList items={[
                    { icon: BookOpenCheck, text: "Kelas tematik Islam" },
                    { icon: Brain, text: "Pelatihan & workshop" },
                    { icon: BookMarked, text: "Kurikulum pembinaan" },
                    { icon: Award, text: "Program intensif" },
                  ]} />
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Target}>Target Peserta</SectionHeader>
                  <TagGrid items={["Mahasiswa", "Profesional muda", "Orang tua", "Guru / Pendidik"]} />
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={BarChart3}>Outcome</SectionHeader>
                  <ProgramList items={[
                    { icon: Medal, text: "Sertifikat program" },
                    { icon: Network, text: "Alumni network" },
                    { icon: Lightbulb, text: "Proyek pembelajaran" },
                  ]} />
                </div>

                <SharedCTA onDonate={() => setDonationView(true)} />
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Pendidikan" />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          THUBA KIDZ
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'kidz'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Kidz</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Belajar Islam itu menyenangkan!</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Program khusus anak usia TK dan SD untuk menumbuhkan keimanan, akhlak mulia, dan kecintaan terhadap Al-Qur'an sejak usia dini melalui metode belajar yang seru dan interaktif.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Palette}>Perencanaan Program</SectionHeader>
                  <ProgramList items={[
                    { icon: BookOpenCheck, text: "Tahsin & tahfidz Al-Qur'an dasar" },
                    { icon: Palette, text: "Kelas seni & kreativitas Islami" },
                    { icon: Puzzle, text: "Permainan & kuis edukatif" },
                    { icon: Tent, text: "Outing & kegiatan alam" },
                  ]} />
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Users}>Untuk Siapa</SectionHeader>
                  <TagGrid items={["Anak TK (usia 4–6 tahun)", "Anak SD (usia 7–12 tahun)", "Orang tua yang peduli pendidikan Islami anak"]} cols={1} />
                </div>


                <SharedCTA onDonate={() => setDonationView(true)} />
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Kidz" />
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════
          THUBA KEMITRAAN
         ═══════════════════════════════════════════════════════ */}
      <Dialog open={activeDialog === 'kemitraan'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="glass-card border-gold/30 max-w-lg overflow-y-auto max-h-[90vh]">
          {!donationView ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif font-bold text-gold text-center">Thuba Kemitraan</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 px-1">
                <p className="text-center text-muted-foreground italic">Berkolaborasi untuk dampak yang lebih luas.</p>

                <div className="space-y-2">
                  <SectionHeader icon={Compass}>Tentang</SectionHeader>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Ruang kolaborasi antara Thuba Project dengan sekolah, masjid, komunitas, dan perusahaan untuk menjalankan program bersama.
                  </p>
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={Link2}>Bentuk Kerja Sama</SectionHeader>
                  <ProgramList items={[
                    { icon: BookOpen, text: "Program edukasi bersama" },
                    { icon: Mic, text: "Event & kajian kolaboratif" },
                    { icon: Megaphone, text: "Media partner" },
                    { icon: Briefcase, text: "CSR & sponsorship" },
                  ]} />
                </div>

                <div className="space-y-3">
                  <SectionHeader icon={BarChart3}>Keuntungan Mitra</SectionHeader>
                  <ProgramList items={[
                    { icon: Users, text: "Exposure komunitas" },
                    { icon: PenTool, text: "Kolaborasi konten" },
                    { icon: Heart, text: "Dampak sosial nyata" },
                  ]} />
                </div>

                {/* Ajukan Kerja Sama CTA */}
                <div className="space-y-3">
                  <a
                    href="https://wa.me/6282310001447?text=Assalamu'alaikum%2C%20kami%20tertarik%20untuk%20menjalin%20kerja%20sama%20kemitraan%20dengan%20Thuba%20Project.%20Mohon%20informasi%20lebih%20lanjut%20mengenai%20skema%20kolaborasi%20yang%20tersedia.%20Jazakumullahu%20khairan."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-5 rounded-xl shadow-lg shadow-green-900/20">
                      <Handshake className="w-4 h-4 mr-2" /> Ajukan Kerja Sama
                    </Button>
                  </a>
                </div>

                <div className="space-y-3 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => setDonationView(true)}
                    className="w-full bg-primary text-black hover:bg-[#F3E5AB] font-bold py-5 rounded-xl shadow-lg shadow-gold/20"
                  >
                    <Heart className="w-4 h-4 mr-2" /> Donasi
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <DonationView onBack={() => setDonationView(false)} backLabel="Thuba Kemitraan" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ecosystem;
